/**
 * NCC: <ncc-hotspot-panel> - the "Side panel" and "Product list" displays of sections/product-hotspots.liquid.
 * Each `_hotspot-product` block renders a hotspot marker plus a panel item (side panel) or a card (product list);
 * this element makes the active hotspot's product current - on click or, on hover-capable devices, hover, and in
 * the theme editor when a hotspot block is selected.
 *
 * Side panel: the active item shows and the others hide (the section's CSS crossfades them).
 * Product list (Emma's "Shop the board", 2026-09-20): every product is a card in a list beside the image; the
 * active card is marked (enlarged and grey on desktop, grey on phones) and brought into view - the list's own
 * scroller on desktop, the page on phones. The card itself links to the product (the first click on a card that
 * is not current selects it, a click on the current one follows the link) and its "+" is Horizon's quick add. The blocks render their markers next to the cards; on
 * connect they are moved onto the image (the hotspots container), so without JavaScript the list is a plain
 * product list.
 */
class NccHotspotPanel extends HTMLElement {
  connectedCallback() {
    this.isList = this.dataset.layout === 'list';
    if (this.isList) this.#adoptMarkers();
    this.addEventListener('click', this.#onClick);
    if (window.matchMedia('(hover: hover)').matches) this.addEventListener('pointerover', this.#onPointerOver);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('pointerover', this.#onPointerOver);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  /** Moves the markers the blocks rendered in the list onto the image. */
  #adoptMarkers() {
    const container = this.querySelector('.hotspots-container');
    if (!container) return;
    for (const marker of this.querySelectorAll('[data-hotspot-panel-marker]')) container.append(marker);
  }

  /**
   * @param {string} id - block id of the hotspot to activate
   * @param {{ reveal?: boolean, source?: 'hotspot' | 'card' | 'editor' }} [options]
   */
  #select(id, { reveal = true, source = 'hotspot' } = {}) {
    for (const trigger of this.querySelectorAll('[data-hotspot-panel-trigger]')) {
      trigger.setAttribute('aria-pressed', trigger.getAttribute('data-hotspot-panel-trigger') === id ? 'true' : 'false');
    }
    /** @type {HTMLElement | null} */
    let active = null;
    const previous = this.isList ? this.querySelector('.ncc-scene-card--active') : null;
    for (const item of this.querySelectorAll('[data-hotspot-panel-item]')) {
      const own = item.getAttribute('data-hotspot-panel-item') === id;
      if (this.isList) {
        item.classList.toggle('ncc-scene-card--active', own);
      } else {
        // the section's CSS crossfades items, so hidden ones stay laid out and inert keeps them out of reach
        item.hidden = !own;
        item.inert = !own;
      }
      if (own && item instanceof HTMLElement) active = item;
    }
    if (this.isList && reveal && active) this.#reveal(active, source, previous);
  }

  /**
   * Brings the active card into view: inside the list on desktop (its top one gap below the list's top edge, so
   * the card grows downwards in place), the page on phones. The desktop target is computed from the FINAL layout -
   * every other card at the inactive height - because the card that was active is still shrinking when this runs
   * and a target read off the live geometry landed the enlarged card part way above the list (measured: 97px).
   * @param {HTMLElement} card
   * @param {'hotspot' | 'card' | 'editor'} source
   * @param {Element | null} [previous] - the card that was active until this selection
   */
  #reveal(card, source, previous = null) {
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const list = card.closest('.ncc-scene__list');
    // the list is its own scroller only in the side-by-side layout (Horizon's desktop breakpoint)
    if (window.matchMedia('(min-width: 990px)').matches) {
      if (!(list instanceof HTMLElement) || list.scrollHeight <= list.clientHeight + 1) return;
      const gap = parseFloat(getComputedStyle(list).getPropertyValue('--ncc-scene-gap')) || 0;
      const cards = [...list.querySelectorAll('.ncc-scene-card')];
      // the resting height, read from a card that was and stays inactive: the card that has just lost its
      // active class is still at its enlarged height at this instant (its shrink is a transition), and sampling
      // it sent the target one enlargement too high (Miles: card 1 active, hover 2 or 3 -> partly hidden)
      const inactive = cards.find((c) => c !== card && c !== previous && !c.classList.contains('ncc-scene-card--active'));
      const inactiveHeight = inactive ? inactive.getBoundingClientRect().height : card.getBoundingClientRect().height;
      const index = cards.indexOf(card);
      const top = index * (inactiveHeight + gap) - gap;
      list.scrollTo({ top: Math.max(0, top), behavior });
    } else if (source !== 'card') {
      // on phones a tapped card is already under the thumb; only a hotspot or the editor brings one up
      card.scrollIntoView({ block: 'center', behavior });
    }
  }

  /** @param {Event} event */
  #triggerFrom(event) {
    return event.target instanceof Element ? event.target.closest('[data-hotspot-panel-trigger]') : null;
  }

  #onClick = (/** @type {Event} */ event) => {
    const trigger = this.#triggerFrom(event);
    if (trigger) {
      this.#select(trigger.getAttribute('data-hotspot-panel-trigger') ?? '');
      return;
    }
    // the card body links to the product: the first click on a card that is not current selects it instead, a
    // click on the current card follows the link (Miles, 2026-09-20)
    const cover = event.target instanceof Element ? event.target.closest('.ncc-scene-card__cover') : null;
    const card = cover?.closest('[data-hotspot-panel-item]');
    if (!cover || !card || card.classList.contains('ncc-scene-card--active')) return;
    if (event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)) return;
    event.preventDefault();
    this.#select(card.getAttribute('data-hotspot-panel-item') ?? '', { source: 'card' });
  };

  #onPointerOver = (/** @type {Event} */ event) => {
    const trigger = this.#triggerFrom(event);
    if (trigger && trigger.getAttribute('aria-pressed') !== 'true') {
      this.#select(trigger.getAttribute('data-hotspot-panel-trigger') ?? '');
    }
  };

  #onBlockSelect = (/** @type {Event & { detail?: { blockId?: string } }} */ event) => {
    const id = event.detail?.blockId;
    if (id && this.querySelector(`[data-hotspot-panel-trigger="${id}"]`)) this.#select(id, { source: 'editor' });
  };
}

if (!customElements.get('ncc-hotspot-panel')) {
  customElements.define('ncc-hotspot-panel', NccHotspotPanel);
}
